import React, { useState } from 'react';
import {
  Clock, Check, X, ChevronRight, User, Briefcase,
  Calendar, Filter, Search, ArrowUpRight, AlertCircle,
  CheckCircle2, XCircle, TimerIcon, Eye, MoreHorizontal
} from 'lucide-react';
import { APP, STATUS_COLORS, StatusBadge, IconPill } from './DashbordApplicationTab';

/* ─── Stage config ───────────────────────────────────────────────────────── */
const STAGES = [
  {
    key: 'pending',
    label: 'Pending Review',
    icon: Clock,
    bg: APP.amberBg,
    color: APP.amber,
    desc: 'Awaiting first review',
  },
  {
    key: 'shortlisted',
    label: 'Shortlisted',
    icon: CheckCircle2,
    bg: APP.skyBg,
    color: APP.sky,
    desc: 'Selected for interview',
  },
  {
    key: 'accepted',
    label: 'Accepted',
    icon: Check,
    bg: APP.greenBg,
    color: APP.green,
    desc: 'Offer extended',
  },
  {
    key: 'rejected',
    label: 'Rejected',
    icon: XCircle,
    bg: APP.redBg,
    color: APP.red,
    desc: 'Not selected',
  },
];

/* ─── Helpers ────────────────────────────────────────────────────────────── */
const avatar = (name, bg = APP.primaryBg, color = APP.primary) => (
  <div
    style={{ backgroundColor: bg, color }}
    className="w-9 h-9 rounded-full text-sm font-bold flex items-center justify-center flex-shrink-0"
  >
    {name?.charAt(0)?.toUpperCase() || '?'}
  </div>
);

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '—';

const daysAgo = (d) => {
  if (!d) return null;
  const diff = Math.floor((Date.now() - new Date(d)) / 86_400_000);
  return diff === 0 ? 'Today' : diff === 1 ? 'Yesterday' : `${diff}d ago`;
};

/* ─── Stage summary pill row ─────────────────────────────────────────────── */
const StageSummaryCards = ({ apps }) => {
  const counts = STAGES.map(({ key, label, icon, bg, color, desc }) => ({
    key,
    label,
    icon,
    bg,
    color,
    desc,
    count: apps.filter((a) => a.status === key).length,
  }));

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
      {counts.map(({ key, label, count, icon, bg, color, desc }) => (
        <div
          key={key}
          style={{ backgroundColor: APP.card, border: `1px solid ${APP.border}` }}
          className="rounded-2xl p-4 flex flex-col gap-3 hover:shadow-sm transition-shadow"
        >
          <IconPill icon={icon} bg={bg} color={color} />
          <div>
            <p style={{ color: APP.text }} className="text-2xl font-bold leading-none">
              {count}
            </p>
            <p style={{ color: APP.textSm }} className="text-xs mt-1">{label}</p>
          </div>
          <p style={{ color: APP.textXs }} className="text-xs">{desc}</p>
        </div>
      ))}
    </div>
  );
};

/* ─── Kanban column ──────────────────────────────────────────────────────── */
const KanbanColumn = ({ stage, apps, onAction }) => {
  const { key, label, icon: Icon, bg, color } = stage;
  const stageApps = apps.filter((a) => a.status === key);

  return (
    <div
      style={{ backgroundColor: APP.card, border: `1px solid ${APP.border}` }}
      className="rounded-2xl flex flex-col min-h-[420px]"
    >
      {/* Column header */}
      <div
        className="flex items-center justify-between px-4 py-3 rounded-t-2xl"
        style={{ borderBottom: `1px solid ${APP.border}` }}
      >
        <div className="flex items-center gap-2">
          <span
            className="w-6 h-6 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: bg }}
          >
            <Icon size={13} style={{ color }} />
          </span>
          <span style={{ color: APP.text }} className="text-sm font-semibold">
            {label}
          </span>
        </div>
        <span
          className="text-[10px] font-bold px-2 py-0.5 rounded-full"
          style={{ backgroundColor: bg, color }}
        >
          {stageApps.length}
        </span>
      </div>

      {/* Cards */}
      <div className="flex-1 p-3 space-y-2.5 overflow-y-auto max-h-[520px]">
        {stageApps.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 gap-2">
            <AlertCircle size={18} style={{ color: APP.textXs }} />
            <p style={{ color: APP.textXs }} className="text-xs text-center">
              No candidates here
            </p>
          </div>
        ) : (
          stageApps.map((app) => (
            <KanbanCard key={app.id} app={app} onAction={onAction} />
          ))
        )}
      </div>
    </div>
  );
};

/* ─── Kanban card ────────────────────────────────────────────────────────── */
const KanbanCard = ({ app, onAction }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const ACTIONS = [
    { label: 'Mark Accepted', status: 'accepted', color: APP.green },
    { label: 'Mark Rejected', status: 'rejected', color: APP.red },
    { label: 'Move to Pending', status: 'pending', color: APP.amber },
  ].filter((a) => a.status !== app.status);

  return (
    <div
      style={{
        backgroundColor: APP.bg,
        border: `1px solid ${APP.border}`,
      }}
      className="rounded-xl p-3 hover:shadow-sm transition-shadow relative group"
    >
      <div className="flex items-start gap-2.5">
        {avatar(app.candidateName)}
        <div className="flex-1 min-w-0">
          <p style={{ color: APP.text }} className="text-xs font-semibold truncate">
            {app.candidateName}
          </p>
          <p style={{ color: APP.textSm }} className="text-[11px] truncate mt-0.5">
            {app.jobTitle}
          </p>
          <p style={{ color: APP.textXs }} className="text-[10px] mt-1">
            {daysAgo(app.date)}
          </p>
        </div>

        {/* More menu */}
        <div className="relative">
          <button
            onClick={() => setMenuOpen((p) => !p)}
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-lg hover:bg-slate-100"
            style={{ color: APP.textSm }}
          >
            <MoreHorizontal size={14} />
          </button>
          {menuOpen && (
            <div
              style={{
                backgroundColor: APP.card,
                border: `1px solid ${APP.border}`,
                boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
              }}
              className="absolute right-0 top-6 z-20 rounded-xl py-1 min-w-[150px]"
            >
              {ACTIONS.map(({ label, status, color }) => (
                <button
                  key={status}
                  onClick={() => {
                    onAction(app.id, status);
                    setMenuOpen(false);
                  }}
                  style={{ color }}
                  className="w-full text-left px-3 py-2 text-xs font-medium hover:bg-slate-50 transition-colors"
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div
        className="flex items-center justify-between mt-2.5 pt-2"
        style={{ borderTop: `1px solid ${APP.borderLight}` }}
      >
        <StatusBadge status={app.status} />
        <span style={{ color: APP.textXs }} className="text-[10px]">
          {fmtDate(app.date)}
        </span>
      </div>
    </div>
  );
};

/* ─── List view row ──────────────────────────────────────────────────────── */
const ListRow = ({ app, onAction }) => (
  <div
    style={{ borderBottom: `1px solid ${APP.borderLight}` }}
    className="flex items-center gap-3 py-3 px-4 hover:bg-slate-50 transition-colors"
  >
    {avatar(app.candidateName)}
    <div className="flex-1 min-w-0">
      <p style={{ color: APP.text }} className="text-sm font-medium truncate">
        {app.candidateName}
      </p>
      <p style={{ color: APP.textSm }} className="text-xs truncate">{app.jobTitle}</p>
    </div>
    <div className="hidden md:flex items-center gap-1 text-xs" style={{ color: APP.textXs }}>
      <Calendar size={11} />
      {fmtDate(app.date)}
    </div>
    <StatusBadge status={app.status} />
    <div className="flex items-center gap-1">
      {app.status === 'pending' && (
        <>
          <button
            onClick={() => onAction(app.id, 'accepted')}
            title="Accept"
            style={{ backgroundColor: APP.greenBg, color: APP.green }}
            className="w-7 h-7 rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity"
          >
            <Check size={13} />
          </button>
          <button
            onClick={() => onAction(app.id, 'rejected')}
            title="Reject"
            style={{ backgroundColor: APP.redBg, color: APP.red }}
            className="w-7 h-7 rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity"
          >
            <X size={13} />
          </button>
        </>
      )}
    </div>
  </div>
);

/* ─── Main PipelineTab ───────────────────────────────────────────────────── */
const PipelineTab = ({ dashboardData }) => {
  const [view, setView] = useState('kanban'); // 'kanban' | 'list'
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [localApps, setLocalApps] = useState(null);

  /* Use local state for optimistic updates, fall back to dashboardData */
  const rawApps = localApps ?? dashboardData?.recentApplications ?? [];

  const apps = rawApps.filter((a) => {
    const matchSearch =
      !search ||
      a.candidateName?.toLowerCase().includes(search.toLowerCase()) ||
      a.jobTitle?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || a.status === filterStatus;
    return matchSearch && matchStatus;
  });

  /* Optimistic status change — in a real app you'd also call your API here */
  const handleAction = (id, newStatus) => {
    setLocalApps((prev) =>
      (prev ?? rawApps).map((a) =>
        a.id === id ? { ...a, status: newStatus } : a
      )
    );
  };

  const pending = rawApps.filter((a) => a.status === 'pending').length;

  return (
    <div>
      {/* Section header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h2 style={{ color: APP.text }} className="text-base font-semibold">
            Candidate Pipeline
          </h2>
          <p style={{ color: APP.textXs }} className="text-xs mt-0.5">
            {pending} candidate{pending !== 1 ? 's' : ''} awaiting your decision
          </p>
        </div>

        {/* View toggle */}
        <div style={{ border: `1px solid ${APP.border}` }} className="flex rounded-xl overflow-hidden self-start">
          {[
            { key: 'kanban', label: 'Board' },
            { key: 'list', label: 'List' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setView(key)}
              style={
                view === key
                  ? { backgroundColor: APP.primary, color: '#fff' }
                  : { backgroundColor: APP.card, color: APP.textMd }
              }
              className="px-4 py-1.5 text-xs font-medium hover:opacity-90 transition-opacity"
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <StageSummaryCards apps={rawApps} />

      {/* Filters bar */}
      <div
        style={{ backgroundColor: APP.card, border: `1px solid ${APP.border}` }}
        className="rounded-2xl px-4 py-3 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center mb-5"
      >
        {/* Search */}
        <div className="relative flex-1 max-w-xs">
          <Search
            size={13}
            className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: APP.textXs }}
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search candidate or job…"
            style={{
              backgroundColor: APP.bg,
              border: `1px solid ${APP.border}`,
              color: APP.text,
            }}
            className="w-full pl-8 pr-3 py-2 text-xs rounded-xl outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>

        {/* Status filter pills */}
        <div className="flex gap-1.5 flex-wrap">
          {['all', ...STAGES.map((s) => s.key)].map((s) => {
            const stage = STAGES.find((st) => st.key === s);
            const active = filterStatus === s;
            return (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                style={
                  active
                    ? {
                        backgroundColor: stage?.bg ?? APP.primaryBg,
                        color: stage?.color ?? APP.primary,
                        border: `1px solid ${stage?.color ?? APP.primary}22`,
                      }
                    : {
                        backgroundColor: APP.bg,
                        color: APP.textMd,
                        border: `1px solid ${APP.border}`,
                      }
                }
                className="px-3 py-1.5 text-xs font-medium rounded-lg capitalize hover:opacity-90 transition-opacity"
              >
                {s === 'all' ? 'All' : s}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Kanban board ── */}
      {view === 'kanban' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {STAGES.map((stage) => (
            <KanbanColumn
              key={stage.key}
              stage={stage}
              apps={apps}
              onAction={handleAction}
            />
          ))}
        </div>
      )}

      {/* ── List view ── */}
      {view === 'list' && (
        <div
          style={{ backgroundColor: APP.card, border: `1px solid ${APP.border}` }}
          className="rounded-2xl overflow-hidden"
        >
          {/* Table head */}
          <div
            className="grid grid-cols-[auto_1fr_auto_auto_auto] gap-3 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider"
            style={{
              color: APP.textXs,
              borderBottom: `1px solid ${APP.border}`,
              backgroundColor: APP.bg,
            }}
          >
            <span className="w-9" />
            <span>Candidate / Role</span>
            <span className="hidden md:block">Applied</span>
            <span>Status</span>
            <span>Actions</span>
          </div>

          {apps.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-14">
              <AlertCircle size={20} style={{ color: APP.textXs }} />
              <p style={{ color: APP.textXs }} className="text-sm">
                No candidates match your filters
              </p>
            </div>
          ) : (
            apps.map((app) => (
              <ListRow key={app.id} app={app} onAction={handleAction} />
            ))
          )}
        </div>
      )}

      {/* Pending callout */}
      {pending > 0 && (
        <div
          style={{
            backgroundColor: APP.amberBg,
            border: `1px solid ${APP.amber}33`,
          }}
          className="rounded-2xl px-4 py-3 mt-5 flex items-center gap-3"
        >
          <TimerIcon size={15} style={{ color: APP.amber }} />
          <p style={{ color: APP.amber }} className="text-xs font-medium flex-1">
            {pending} application{pending !== 1 ? 's are' : ' is'} pending review — act
            before candidates lose interest.
          </p>
          <button
            onClick={() => setFilterStatus('pending')}
            style={{ color: APP.amber }}
            className="text-xs font-semibold flex items-center gap-1 hover:underline"
          >
            Review now <ChevronRight size={12} />
          </button>
        </div>
      )}
    </div>
  );
};

export default PipelineTab;